/* eslint-disable max-classes-per-file */
import Cookies from "js-cookie";
import getApiDomain from "@utils/services/apiDomain";
import { getInAppAutoCookie } from "./authCookieParser";
import { send } from "./fetch";
import { updateStoreWithServerContext } from "./initialization";
import { ServerContextState } from "./redux/slices/serverContext";
import { captureException, initializeSentry } from "./sentry";
import type { Context } from "@noom/meristem-context";
import { filterApiParameters, getSearchQuery } from "./urlParams";
import { getMeristemContext } from "./meristemContext";
import { isHM } from "./userSegment";
import store from "./redux/store";

export class FetchServerContextError extends Error {}

export class FetchInAppServerContextError extends Error {}

/**
 * Validates the server context. Returns false if the context is undefined, empty or does not have the critical properties;
 */
export function isServerContextValid(context: ServerContextState) {
  // Protect from undefined toStringed values
  if (context?.user_id === "undefined") {
    return false;
  }

  return !!context?.user_id;
}

/**
 * Look through serverContext and if certain fields are found, set them in
 * the cookie.
 */
function setCampaignCookiesAndReturnContext(serverContext: ServerContextState) {
  if (!isServerContextValid(serverContext)) {
    throw Error(`Invalid context: ${JSON.stringify(serverContext)}`);
  }

  const { campaign_promo_cookie, session_cookie_domain } = serverContext;

  if (campaign_promo_cookie?.active) {
    Cookies.set("campaign_promo", campaign_promo_cookie.value, {
      domain: session_cookie_domain,
    });
  } else {
    Cookies.remove("campaign_promo");
  }

  return serverContext;
}

/**
 * Look through meristemContext and if certain fields are found, set them in
 * the cookie.
 */
export function setCookiesFromMeristemContext(meristemContext: Context) {
  if (meristemContext.route_id) {
    Cookies.set("_routeId", meristemContext.route_id, {
      domain: getApiDomain(),
    });
  }

  if (meristemContext.language_code) {
    Cookies.set("_languageCode", meristemContext.language_code, {
      domain: getApiDomain(),
    });
  }
}

async function fetchContext(
  URL: string,
  useInAppAuthCookie: boolean
): Promise<ServerContextState> {
  // Load in-app auth cookies, if available
  let authCookie: Record<string, string>;
  if (useInAppAuthCookie) {
    authCookie = getInAppAutoCookie();
  }

  const method = useInAppAuthCookie ? "POST" : "GET";
  try {
    const context: ServerContextState = await send(method, URL, authCookie);

    return setCampaignCookiesAndReturnContext(context);
  } catch (error) {
    initializeSentry(); // In case Sentry is not initialized yet
    captureException(error, [method, "server context fetch detailed"]);

    if (useInAppAuthCookie) {
      throw new FetchInAppServerContextError(error);
    } else {
      throw new FetchServerContextError(error);
    }
  }
}

// A bit of a hack to avoid duplicate requests. Don't want to invest too much
// time as this is going away.
let lastContextPath: string;

const contextCache: Record<string, Promise<ServerContextState>> = {};

/**
 * Fetch the serverContext from the Growth API.
 * Currently has special handling in app context fetching.
 */
export default function getContext() {
  const {
    context_type: contextType,
    language_code: language,
    route_id: routeId,
  } = getMeristemContext();

  const { pathname, search } = window.location;

  lastContextPath = pathname;
  let requestPath = pathname.replace(/^\/|\/$/g, "");

  const isUnknownPPath = !contextType && pathname.startsWith("/p/");
  if (["landing", "main-survey"].includes(contextType) || isUnknownPPath) {
    if (isHM()) {
      requestPath = `programs/mood/${routeId}`;
    } else {
      requestPath = `${language}/programs/health-weight/${routeId}`;
    }
  }

  // Use v2 routing for /add-ons, /offers, and /in-app-offers
  if (
    ["add-ons", "offers", "noom-premium"].some((str) =>
      requestPath.includes(str)
    )
  ) {
    requestPath += "/v2";
  }

  const queryParams = new URLSearchParams(search);
  // If we have a upid available, also send that along
  const { upid } = store.getState();
  if (upid) {
    queryParams.set("upid", upid);
  }
  filterApiParameters(queryParams);

  const pathParts = [requestPath, getSearchQuery(queryParams)].filter(Boolean);

  const URL = `/api/context/v2/${pathParts.join("/")}`;

  if (!contextCache[URL]) {
    const useInAppAuthCookie =
      requestPath.includes("in-app-offers") || requestPath.includes("course");

    contextCache[URL] = fetchContext(URL, useInAppAuthCookie);
  }
  return contextCache[URL];
}

/**
 * Refetch serverContext and mount it where expected.
 */
export async function reloadServerContext() {
  // NOTE(Patrick): When loading the first route, don't refetch the serverContext
  // because we fetch it in initializeApplication.
  if (window.location.pathname === lastContextPath) {
    return null;
  }
  return loadServerContext();
}

export async function loadServerContext() {
  const serverContext = await getContext();
  (window as any).serverContext = serverContext;
  updateStoreWithServerContext(serverContext);
  return serverContext;
}
